import React, { useState, useRef, useEffect } from "react";
import { Container, Row, Col, Form } from "react-bootstrap";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import emailjs from '@emailjs/browser';
import Particle from "../Particle";
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import {
  AiFillGithub,
  AiFillLinkedin,
  AiOutlineTwitter,
  AiOutlineInstagram,
  AiOutlineFacebook,
} from "react-icons/ai";
import "./Contact.css";

function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    message: ""
  });
  const [formErrors, setFormErrors] = useState({
    name: "",
    phone: "",
    email: "",
    message: ""
  });
  const [formStatus, setFormStatus] = useState(null);
  const formRef = useRef(null);

  // Set up animations with intersection observer
  const controls = useAnimation();
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  // Initialize EmailJS
  useEffect(() => {
    emailjs.init("XqrKNUIzAVKJPw_xe");
  }, []);

  useEffect(() => {
    if (inView) {
      controls.start("visible");
    }
  }, [controls, inView]);

  const validateForm = () => {
    let isValid = true;
    const newErrors = {
      name: "",
      phone: "",
      email: "",
      message: ""
    };

    // Name validation (max 14 characters)
    if (formData.name.trim() === "") {
      newErrors.name = "Name is required";
      isValid = false;
    } else if (formData.name.length > 14) {
      newErrors.name = "Name must be 14 characters or less";
      isValid = false;
    }

    // Email validation with more strict regex
    if (formData.email.trim() === "") {
      newErrors.email = "Email is required";
      isValid = false;
    } else {
      // More comprehensive email validation regex
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailRegex.test(formData.email)) {
        newErrors.email = "Please enter a valid email address";
        isValid = false;
      }
    }

    // Message validation
    if (formData.message.trim() === "") {
      newErrors.message = "Message is required";
      isValid = false;
    }

    setFormErrors(newErrors);
    return isValid;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
    
    // Clear error when user starts typing in field
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form before submission
    if (!validateForm()) {
      return;
    }
    
    setFormStatus("sending");
    
    try {
      // Send to AI-powered smart contact endpoint
      const response = await fetch("/api/smart-contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          message: formData.message,
        }),
      });

      const result = await response.json();

      if (result.success) {
        // Also send via EmailJS as backup
        try {
          await emailjs.sendForm(
            "service_l35lr0m",  // Service ID
            "template_58upqg5", // Template ID
            formRef.current
          );
        } catch (emailError) {
          console.warn("EmailJS backup failed:", emailError);
        }

        // Show success with AI auto-response
        setFormStatus("success");
        
        // Store auto-response to show to user
        if (result.autoResponse) {
          sessionStorage.setItem("contact_auto_response", result.autoResponse);
        }

        setFormData({
          name: "",
          phone: "",
          email: "",
          message: ""
        });
        
        // Reset status after 8 seconds (longer to read AI response)
        setTimeout(() => setFormStatus(null), 8000);
      } else {
        throw new Error(result.error || "Failed to send message");
      }
    } catch (error) {
      console.error("Failed to send message:", error);
      setFormStatus("error");
      setTimeout(() => setFormStatus(null), 5000);
    }
  };

  const socialLinks = [
    {
      icon: <AiFillGithub />,
      title: "GitHub",
      link: "https://github.com/marwaniiwael18"
    },
    {
      icon: <AiFillLinkedin />,
      title: "LinkedIn",
      link: "https://www.linkedin.com/in/wael-marwani-/"
    },
    {
      icon: <AiOutlineTwitter />,
      title: "Twitter",
      link: "https://x.com/wael93186"
    },
    {
      icon: <AiOutlineInstagram />,
      title: "Instagram",
      link: "https://www.instagram.com/waeel_marouanii/"
    },
    {
      icon: <AiOutlineFacebook />,
      title: "Facebook",
      link: "https://www.facebook.com/World.Wide.Server08/"
    }
  ];

  // Animation variants with corrected easing function
  const fadeUpVariant = {
    hidden: { opacity: 0, y: 50 },
    visible: { 
      opacity: 1, 
      y: 0, 
      transition: {
        duration: 0.8, 
        ease: [0.6, 0.05, 0.01, 0.9] // Fixed cubic-bezier values (removed negative value)
      } 
    }
  };
  
  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  return (
    <section className="contact-section">
      <Particle className="particles-contact" />
      
      <div className="shape-divider top-divider">
        <svg data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none">
          <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" className="shape-fill"></path>
        </svg>
      </div>

      <Container>
        {/* Header Section with enhanced animation */}
        <motion.div
          ref={ref}
          animate={controls}
          initial="hidden"
          variants={fadeUpVariant}
          className="contact-header"
        >
          <div className="header-background-glow"></div>
          <h1 className="main-title">
            Let's <span className="gradient-text">Connect</span>
          </h1>
          <p className="subtitle">
            Have a project in mind or just want to chat? I'd love to hear from you!
          </p>
        </motion.div>

        {/* Contact Content */}
        <motion.div 
          className="contact-container"
          ref={ref}
          animate={controls}
          initial="hidden"
          variants={staggerContainer}
        >
          <Row className="contact-row">
            {/* Contact Form */}
            <Col lg={7} className="contact-form-column">
              <motion.div 
                className="contact-form-card"
                variants={fadeUpVariant}
              >
                <div className="form-header">
                  <h2 className="form-title">Drop Me a Message</h2>
                </div>
                
                <Form ref={formRef} onSubmit={handleSubmit} className="contact-form">
                  <Row>
                    <Col md={6}>
                      <motion.div variants={fadeUpVariant} className="form-group floating-label-group">
                        <Form.Control
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder=" "
                          required
                          className="floating-input"
                        />
                        <label className="floating-label">Your Name</label>
                        <div className="focus-border"></div>
                        {formErrors.name && <div className="error-message">{formErrors.name}</div>}
                      </motion.div>
                    </Col>
                    
                    <Col md={6}>
                      <motion.div variants={fadeUpVariant} className="form-group">
                        <PhoneInput
                          country="fr"
                          value={formData.phone}
                          onChange={phone => setFormData({...formData, phone})}
                          placeholder="Phone Number"
                          inputProps={{
                            name: 'phone',
                            required: false,
                            className: "form-control"
                          }}
                          containerClass="phone-input-container"
                          buttonClass="country-dropdown-button"
                        />
                        {formErrors.phone && <div className="error-message">{formErrors.phone}</div>}
                      </motion.div>
                    </Col>
                  </Row>
                  
                  <motion.div variants={fadeUpVariant} className="form-group floating-label-group">
                    <Form.Control
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder=" "
                      required
                      className="floating-input"
                    />
                    <label className="floating-label">Email Address</label>
                    <div className="focus-border"></div>
                    {formErrors.email && <div className="error-message">{formErrors.email}</div>}
                  </motion.div>
                  
                  <motion.div variants={fadeUpVariant} className="form-group floating-label-group">
                    <Form.Control
                      as="textarea"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={5}
                      placeholder=" "
                      required
                      className="floating-input"
                    />
                    <label className="floating-label">Your Message</label>
                    <div className="focus-border textarea-border"></div>
                  </motion.div>
                  
                  <motion.div 
                    variants={fadeUpVariant}
                    className="form-button-wrapper"
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                  >
                    <button 
                      type="submit" 
                      className="form-submit-button"
                      disabled={formStatus === "sending"}
                    >
                      {formStatus === "sending" ? (
                        <span className="button-text">Sending...</span>
                      ) : (
                        <>
                          <span className="button-text">Send Message</span>
                        </>
                      )}
                      <div className="button-background"></div>
                    </button>
                  </motion.div>
                  
                  {formStatus === "success" && (
                    <motion.div 
                      className="form-success-message"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5 }}
                    >
                      <div className="success-icon">✓</div>
                      <p><strong>Message sent successfully!</strong></p>
                      <p className="ai-response">
                        🤖 {sessionStorage.getItem("contact_auto_response") || "Thank you for your message! I'll get back to you soon."}
                      </p>
                    </motion.div>
                  )}
                  {formStatus === "error" && (
                    <motion.div 
                      className="form-error-message"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5 }}
                    >
                      <div className="error-icon">✕</div>
                      <p>Oops! Something went wrong. Please try again or email me directly.</p>
                    </motion.div>
                  )}
                </Form>
              </motion.div>
            </Col>

            {/* Connect With Me Card */}
            <Col lg={5} className="contact-social-column">
              <motion.div 
                className="contact-social-card"
                variants={fadeUpVariant}
              >
                <h2 className="social-title">Connect With Me</h2>
                
                <div className="social-description">
                  <p>Follow me on social media to stay updated with my latest projects and activities.</p>
                </div>
                
                <motion.div 
                  className="social-icons-large"
                  variants={staggerContainer}
                  initial="hidden"
                  animate="visible"
                >
                  {socialLinks.map((social, index) => (
                    <motion.a 
                      key={index}
                      href={social.link}
                      target="_blank"
                      rel="noreferrer"
                      className="social-icon-large"
                      aria-label={social.title}
                      variants={{
                        hidden: { opacity: 0, x: -20 },
                        visible: { 
                          opacity: 1, 
                          x: 0,
                          transition: { duration: 0.5, delay: index * 0.1 }
                        }
                      }}
                      whileHover={{ 
                        x: 10, 
                        backgroundColor: "rgba(137, 43, 226, 0.4)"
                      }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <div className="social-icon-container">
                        {social.icon}
                      </div>
                      <span>{social.title}</span>
                    </motion.a>
                  ))}
                </motion.div>
              </motion.div>
            </Col>
          </Row>

          {/* Enhanced Map Section */}
          <motion.div
            className="map-section"
            variants={fadeUpVariant}
          >
            <h2 className="map-title">Find Me Here</h2>
            <div className="map-container">
              <div className="map-overlay">
                <div className="map-pin-container">
                  <div className="map-pin"></div>
                  <div className="map-pin-ripple"></div>
                </div>
              </div>
              <iframe 
                title="Location Map"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d83998.94722687861!2d2.277019841406253!3d48.85882754648417!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47e66e1f06e2b411%3A0x40b82c3688c9460!2sParis%2C%20France!5e0!3m2!1sen!2stn!4v1620330979765!5m2!1sen!2stn"
                width="100%" 
                height="350" 
                style={{ border: 0 }} 
                allowFullScreen="" 
                loading="lazy"
              ></iframe>
            </div>
          </motion.div>
        </motion.div>
      </Container>

      <div className="shape-divider bottom-divider">
        <svg data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none">
          <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" className="shape-fill"></path>
        </svg>
      </div>
    </section>
  );
}

export default Contact;